'use client'

import clsx from 'clsx'
import { Link, useLocation } from 'react-router-dom'
import { useLayout } from '../../core'
import { Navbar } from './Navbar'
import { Header } from './Header'
import { useEffect, useState } from 'react'
import { getBreadcrumbName } from './breadcrumbService'
import { KTIcon, toAbsoluteUrl } from '../../../helpers'
import CapitalizedText from '../../../../app/modules/global/capitalizedText'
import { usePermissions } from '../../../../app/modules/auth/hooks/usePermissions'

export function HeaderWrapper() {
    const { config, classes } = useLayout()
    const location = useLocation()
    const [dynamicName, setDynamicName] = useState<string | null>(null)
    const { isAtendente, isSuperAdmin} = usePermissions()

    const pathParts = location.pathname.split('/').filter(Boolean)

    useEffect(() => {
        const fetchName = async () => {
            const name = await getBreadcrumbName(location.pathname)
            setDynamicName(name)
        }

        fetchName()
    }, [location.pathname])

    if (!config.app?.header?.display) {
        return null
    }

    return (
        <>
            <style>{`
                @media (max-width: 768px) {
                    #kt_app_header {
                        width: 100% !important;
                        overflow-x: auto !important;
                        overflow-y: hidden !important;
                        -webkit-overflow-scrolling: touch;
                        scrollbar-width: thin;
                        scrollbar-color: #009FE3 #f1f1f1;
                    }
                    
                    #kt_app_header::-webkit-scrollbar {
                        height: 6px;
                    }
                    
                    #kt_app_header::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 3px;
                    }
                    
                    #kt_app_header::-webkit-scrollbar-thumb {
                        background: #009FE3;
                        border-radius: 3px;
                    }
                    
                    #kt_app_header::-webkit-scrollbar-thumb:hover {
                        background: #007bb5;
                    }
                    
                    #kt_app_header .breadcrumb {
                        flex-wrap: nowrap !important;
                        min-width: max-content;
                    }
                    
                    #kt_app_header .breadcrumb li {
                        flex-shrink: 0;
                    }
                }
            `}</style>
            <div id="kt_app_header" className="app-header">
                <div
                    id="kt_app_header_container"
                    className={clsx(
                        'app-container flex-lg-grow-1',
                        classes.headerContainer.join(' '),
                        config.app?.header?.default?.containerClass
                    )}
                >


                <nav aria-label="breadcrumb" className="my-3 d-flex align-items-center row m-0 g-2">
                    <div className="col-md-12 p-0">

                    <h2 className="page-title mb-0 breadt">
                        <CapitalizedText text={
                            (() => {
                                const lastPart = pathParts[pathParts.length - 1]

                                if (pathParts[0] === 'associados') {
                                    return 'Associados'
                                }

                                if (
                                    (pathParts[0] === 'tratamentos' || pathParts[0] === 'debitos') &&
                                    !isNaN(Number(lastPart))
                                ) {
                                    return pathParts[0] === 'tratamentos'
                                        ? 'Tratamentos do Associado'
                                        : 'Débitos Detalhados'
                                }

                                if (
                                    dynamicName &&
                                    !['tratamentos', 'debitos'].includes(pathParts[0]) &&
                                    !isNaN(Number(lastPart))
                                ) {
                                    return dynamicName
                                }

                                // Traduzir "update-password" para português
                                if (lastPart === 'update-password') {
                                    return 'Atualizar Senha'
                                }

                                if (lastPart === 'dashboard') {
                                    return 'Início'
                                }
                                
                                return lastPart.replace(/-/g, ' ')
                            })()
                        } />
                    </h2>
                    </div>

                    <ol className="breadcrumb d-flex align-items-center">
                        <li className="breadcrumb-item d-flex align-items-center">
                            <Link to="/dashboard">{pathParts[0] === 'dashboard' ? 'Home' : 'Início'}</Link>
                            {pathParts.length > 0 && (
                                <span className="mx-2">{'>'}</span>
                            )}
                        </li>
                        {pathParts.map((part, index) => {
                            if (pathParts[0] === 'associados' && index > 1) {
                                return null
                            }
                            
                            const isLast = index === pathParts.length - 1 || (pathParts[0] === 'associados' && index === 1)
                            let to = `/${pathParts.slice(0, index + 1).join('/')}`

                            let label = part

                            const isTratamentosOuDebitos = pathParts[0] === 'tratamentos' || pathParts[0] === 'debitos'
                            const isProcessos = pathParts[0] === 'processos'
                            const isNumero = !isNaN(Number(part))

                            if (isTratamentosOuDebitos && index === 1 && isNumero) {
                                label = pathParts[0] === 'tratamentos'
                                    ? 'Prontuários'
                                    : 'Débitos Detalhados do Associado'
                                
                                if (pathParts[0] === 'tratamentos') {
                                    const userId = localStorage.getItem('currentUserId')
                                    if (userId) {
                                        to = `/tratamentos/${userId}`
                                    }
                                }
                            } else if (isProcessos && index === 1 && isNumero && isLast) {
                                // Para processos, usar o nome dinâmico quando disponível
                                label = dynamicName || '...'
                            } else if (
                                isLast &&
                                dynamicName &&
                                !isTratamentosOuDebitos &&
                                !isProcessos &&
                                isNumero
                            ) {
                                label = dynamicName
                            } else {
                                label = label.replace(/-/g, ' ')
                            }
                            if (part === 'dashboard') {
                                label = 'Início'
                            }
                            const breadcumbWithoutLink = isLast || part.toLowerCase() === 'editar' || (pathParts[0] === 'tratamentos' && index === 2) || (label == 'Prontuários' && isAtendente() && !isSuperAdmin());

                            let content;
                            if (breadcumbWithoutLink) {
                                content = (
                                    <span className="breadcrumb-item active" aria-current="page">
                                        <CapitalizedText text={label} />
                                    </span>
                                );
                            } else {
                                content = (
                                    <Link to={to} className="breadcrumb-item">
                                        <CapitalizedText text={label} />
                                    </Link>
                                );
                            }

                            // Remover link do "profile" e mostrar como texto estático
                            if (part === 'profile') {
                                return (
                                    <li key={to} className="d-flex align-items-center">
                                        <span className="breadcrumb-item active" aria-current="page">
                                            <CapitalizedText text="Perfil" />
                                        </span>
                                        {!isLast && (
                                            <span className="mx-2">{'>'}</span>
                                        )}
                                    </li>
                                )
                            }
                            
                            return (
                                <li key={to} className="d-flex align-items-center">
                                    {content}
                                    {!isLast && <span className="mx-2">{'>'}</span>}
                                </li>
                            );

                            // return (
                            //     <li key={to} className="d-flex align-items-center">
                            //         {isLast || part.toLowerCase() === 'editar' || (pathParts[0] === 'tratamentos' && index === 2) ? (
                            //             <span className="breadcrumb-item active" aria-current="page">
                            //                 <CapitalizedText text={label} />
                            //             </span>
                            //         ) : (
                            //             <Link to={to} className="breadcrumb-item">
                            //                 <CapitalizedText text={label} />
                            //             </Link>
                            //         )}
                            //         {!isLast && (
                            //             <span className="mx-2">{'>'}</span>
                            //         )}
                            //     </li>
                            // )
                        })}



                    </ol>
                </nav>

                <div
                    id="kt_app_header_wrapper"
                    className="d-flex align-items-stretch justify-content-between flex-lg-grow-1"
                >
                    {config.app.header.default?.content === 'menu' &&
                        config.app.header.default.menu?.display && (
                            <div
                                className="app-header-menu app-header-mobile-drawer align-items-stretch"
                                data-kt-drawer="true"
                                data-kt-drawer-name="app-header-menu"
                                data-kt-drawer-activate="{default: true, lg: false}"
                                data-kt-drawer-overlay="true"
                                data-kt-drawer-width="225px"
                                data-kt-drawer-direction="end"
                                data-kt-drawer-toggle="#kt_app_header_menu_toggle"
                                data-kt-swapper="true"
                                data-kt-swapper-mode="{default: 'append', lg: 'prepend'}"
                                data-kt-swapper-parent="{default: '#kt_app_body', lg: '#kt_app_header_wrapper'}"
                            >
                                <Header />
                            </div>
                        )}
                    <Navbar />
                </div>
            </div>
        </div>
        </>
    )
}
